import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../hooks/useAuth";
import "./admin.css";

// ==================== PAGE CONFIGURATIONS ====================

const PAGE_CONFIGS = {
  onboarding: {
    title: "🎮 Onboarding Game",
    description: "Interactive onboarding road with video modules",
    firebaseDoc: "onboarding_videos",
    sections: [
      { name: "START", icon: "🎯" },
      { name: "BELIEVE PHILOSOPHY", icon: "🛡️" },
      { name: "FOLLOW SYSTEM", icon: "👔" },
      { name: "BUILD OUTLET", icon: "⌚" },
    ],
    modules: [
      { id: "m0", title: "Intro", section: "START" },
      { id: "m1", title: "Multi Handed", section: "BELIEVE PHILOSOPHY" },
      { id: "m2", title: "10% / 3 Rules / 3 Goals", section: "BELIEVE PHILOSOPHY" },
      { id: "m3", title: "Self Improvement", section: "BELIEVE PHILOSOPHY" },
      { id: "m4", title: "Observe 4 Recruits", section: "FOLLOW SYSTEM" },
      { id: "m5", title: "Observe 4 Clients", section: "FOLLOW SYSTEM" },
      { id: "m6", title: "Get License", section: "FOLLOW SYSTEM" },
      { id: "m7", title: "1 Direct Registration", section: "FOLLOW SYSTEM" },
      { id: "m8", title: "9 Recruits", section: "BUILD OUTLET" },
      { id: "m9", title: "45,000 Personal Points", section: "BUILD OUTLET" },
      { id: "m10", title: "3 Licenses", section: "BUILD OUTLET" },
      { id: "m11", title: "15 Registrations on Base", section: "BUILD OUTLET" },
    ],
    tiers: ["free", "paid"],
  },
  
  trackMyLicense: {
    title: "🪪 Track My License",
    description: "Step-by-step licensing journey with chapter videos",
    firebaseDoc: "track_my_license_videos",
    sections: [
      { name: "Licensing Chapters", icon: "📚" }
    ],
    modules: [
      { id: "ch1", title: "Chapter 1 - Getting Started", section: "Licensing Chapters" },
      { id: "ch2", title: "Chapter 2 - Pre-Licensing", section: "Licensing Chapters" },
      { id: "ch3", title: "Chapter 3 - Agent Agreement", section: "Licensing Chapters" },
      { id: "ch4", title: "Chapter 4 - Pass The Test", section: "Licensing Chapters" },
      { id: "ch5", title: "Chapter 5 - Fingerprints", section: "Licensing Chapters" },
      { id: "ch6", title: "Chapter 6 - NIPR Application", section: "Licensing Chapters" },
      { id: "ch7", title: "Chapter 7 - License Certificate", section: "Licensing Chapters" },
      { id: "ch8", title: "Chapter 8 - WFG Launch Login", section: "Licensing Chapters" },
      { id: "ch9", title: "Chapter 9 - Continuing Education", section: "Licensing Chapters" },
      { id: "ch10", title: "Chapter 10 - Anti-Money Laundering", section: "Licensing Chapters" },
      { id: "ch11", title: "Chapter 11 - Agent Agreement Approval", section: "Licensing Chapters" },
      { id: "ch12", title: "Chapter 12 - E&O Platform", section: "Licensing Chapters" },
      { id: "ch13", title: "Chapter 13 - Direct Deposit", section: "Licensing Chapters" },
      { id: "ch14", title: "Chapter 14 - Final Steps", section: "Licensing Chapters" },
    ],
    tiers: ["main"],
  },

  crashCourse: {
    title: "⚡ Crash Course",
    description: "Quick licensing prep videos and resources",
    firebaseDoc: "crash_course_videos",
    sections: [
      { name: "Course Videos", icon: "🎓" }
    ],
    modules: [
      { id: "intro", title: "Course Introduction", section: "Course Videos" },
      { id: "overview", title: "Licensing Overview", section: "Course Videos" },
      { id: "studyTips", title: "Study Tips & Strategies", section: "Course Videos" },
      { id: "examPrep", title: "Exam Preparation", section: "Course Videos" },
      { id: "quickReview", title: "Quick Review Session", section: "Course Videos" },
    ],
    tiers: ["main"],
  },

  licensingDocuments: {
    title: "📄 Licensing Documents",
    description: "Important licensing paperwork and resources",
    firebaseDoc: "licensing_documents",
    sections: [
      { name: "Documents", icon: "📋" }
    ],
    modules: [
      { id: "licensingGrid", title: "Licensing Grid", section: "Documents" },
      { id: "unifiedLic", title: "Unified Lic", section: "Documents" },
      { id: "crashCoursePDF", title: "Licensing Crash Course (PDF)", section: "Documents" },
    ],
    tiers: ["main"],
  },

  fileVault: {
    title: "🗂️ File Vault",
    description: "Complete resource library for all agents",
    firebaseDoc: "file_vault",
    sections: [
      { name: "Licensing Resources", icon: "🪪" },
      { name: "Forms & Documents", icon: "🗂️" },
      { name: "Presentations", icon: "📽️" },
      { name: "Presentations - All Agents", icon: "👥" },
      { name: "Broker", icon: "🧭" },
      { name: "Power Partners", icon: "⚡" },
      { name: "Maxout Monday", icon: "🚀" },
      { name: "Napkin Presentation", icon: "🧻" },
      { name: "Trainer Manuals", icon: "📘" },
      { name: "Assistant Documents", icon: "📝" },
      { name: "Canada Presentations", icon: "🍁" },
      { name: "Spanish Presentations", icon: "🇪🇸" },
    ],
    modules: [],
    tiers: ["main"],
    useDynamicSections: true, // Sections can have multiple items
  },
};

export default function UnifiedVideoManager() {
  const { isAdmin } = useAuth();
  const [activePage, setActivePage] = useState("onboarding");
  const [activeTier, setActiveTier] = useState("free");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [expandedSections, setExpandedSections] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const currentConfig = PAGE_CONFIGS[activePage];

  useEffect(() => {
    loadAllData();
    // Expand all sections by default
    const defaultExpanded = {};
    Object.keys(PAGE_CONFIGS).forEach(pageKey => {
      PAGE_CONFIGS[pageKey].sections.forEach(section => {
        defaultExpanded[`${pageKey}-${section.name}`] = true;
      });
    });
    setExpandedSections(defaultExpanded);
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const loadedData = {};
      
      for (const [pageKey, config] of Object.entries(PAGE_CONFIGS)) {
        const docRef = doc(db, "config", config.firebaseDoc);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          loadedData[pageKey] = docSnap.data();
        } else {
          // Initialize empty structure
          loadedData[pageKey] = initializePageData(config);
        }
      }
      
      setData(loadedData);
    } catch (error) {
      console.error("Error loading data:", error);
      showMessage("Error loading data: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const initializePageData = (config) => {
    const pageData = {};
    
    if (config.useDynamicSections) {
      // File Vault: sections with items
      config.sections.forEach(section => {
        pageData[section.name] = [];
      });
    } else {
      // Other pages: modules with videos
      config.tiers.forEach(tier => {
        pageData[tier] = {};
        config.modules.forEach(module => {
          pageData[tier][module.id] = {
            title: "",
            url: "",
            description: "",
            thumbnail: ""
          };
        });
      });
    }
    
    return pageData;
  };

  const handleSave = async (pageKey) => {
    setSaving(true);
    try {
      const config = PAGE_CONFIGS[pageKey];
      const docRef = doc(db, "config", config.firebaseDoc);
      await setDoc(docRef, data[pageKey] || {});
      showMessage(`✅ ${config.title} saved successfully!`, "success");
    } catch (error) {
      console.error("Error saving:", error);
      showMessage("❌ Error saving: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const updateModuleField = (pageKey, tier, moduleId, field, value) => {
    setData(prev => ({
      ...prev,
      [pageKey]: {
        ...prev[pageKey],
        [tier]: {
          ...prev[pageKey]?.[tier],
          [moduleId]: {
            ...prev[pageKey]?.[tier]?.[moduleId],
            [field]: value
          }
        }
      }
    }));
  };

  const updateFileVaultSection = (pageKey, sectionName, items) => {
    setData(prev => ({
      ...prev,
      [pageKey]: {
        ...prev[pageKey],
        [sectionName]: items
      }
    }));
  };

  const addFileVaultItem = (pageKey, sectionName) => {
    const currentItems = data[pageKey]?.[sectionName] || [];
    const newItem = {
      id: `item-${Date.now()}`,
      title: "",
      href: "",
      thumb: "",
      type: "card"
    };
    updateFileVaultSection(pageKey, sectionName, [...currentItems, newItem]);
  };

  const updateFileVaultItem = (pageKey, sectionName, itemId, field, value) => {
    const currentItems = data[pageKey]?.[sectionName] || [];
    const updatedItems = currentItems.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
    updateFileVaultSection(pageKey, sectionName, updatedItems);
  };

  const deleteFileVaultItem = (pageKey, sectionName, itemId) => {
    const currentItems = data[pageKey]?.[sectionName] || [];
    const updatedItems = currentItems.filter(item => item.id !== itemId);
    updateFileVaultSection(pageKey, sectionName, updatedItems);
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const validateAndConvertUrl = (url) => {
    if (!url || url.trim() === "") {
      return { isValid: true, convertedUrl: "", type: "empty", message: "No URL provided" };
    }

    try {
      if (url.includes("firebasestorage.googleapis.com")) {
        return { isValid: true, convertedUrl: url, type: "firebase", message: "🔥 Firebase" };
      }
      if (url.match(/\.(mp4|webm|ogg|pdf|doc|docx|ppt|pptx)(\?|$)/i)) {
        return { isValid: true, convertedUrl: url, type: "direct", message: "📄 Direct File" };
      }
      if (url.includes("youtube.com") || url.includes("youtu.be")) {
        let videoId;
        if (url.includes("youtu.be/")) {
          videoId = url.split("youtu.be/")[1].split("?")[0];
        } else if (url.includes("watch?v=")) {
          videoId = url.split("watch?v=")[1].split("&")[0];
        }
        if (videoId) {
          const embedUrl = `https://www.youtube.com/embed/${videoId}`;
          return { isValid: true, convertedUrl: embedUrl, type: "youtube", message: "🔄 YouTube" };
        }
      }
      if (url.includes("youtube.com/embed/")) {
        return { isValid: true, convertedUrl: url, type: "youtube", message: "✅ YouTube" };
      }
      if (url.includes("vimeo.com")) {
        return { isValid: true, convertedUrl: url, type: "vimeo", message: "✅ Vimeo" };
      }
      if (url.includes("docs.google.com")) {
        return { isValid: true, convertedUrl: url, type: "google", message: "📊 Google Docs" };
      }
      if (url.startsWith("http://") || url.startsWith("https://")) {
        return { isValid: true, convertedUrl: url, type: "external", message: "🔗 External" };
      }
      return { isValid: false, convertedUrl: url, type: "unknown", message: "⚠️ Unknown" };
    } catch (error) {
      return { isValid: false, convertedUrl: url, type: "error", message: "❌ Invalid" };
    }
  };

  const handleVideoUpload = async (pageKey, tier, moduleId, file) => {
    if (!file) return;

    const uploadKey = `${pageKey}-${tier}-${moduleId}`;
    const storageRef = ref(storage, `${pageKey}/${tier}/${moduleId}_${Date.now()}.mp4`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(prev => ({ ...prev, [uploadKey]: progress }));
      },
      (error) => {
        console.error("Upload error:", error);
        showMessage("❌ Upload failed: " + error.message, "error");
        setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        updateModuleField(pageKey, tier, moduleId, "url", downloadURL);
        showMessage("✅ Video uploaded successfully!", "success");
        setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
      }
    );
  };

  const getStatusBadge = (pageKey, tier, moduleId) => {
    const moduleData = data[pageKey]?.[tier]?.[moduleId];
    if (moduleData?.url && moduleData?.title) {
      return <span className="status-badge active">✓ Active</span>;
    } else if (moduleData?.url || moduleData?.title) {
      return <span className="status-badge warning">! Incomplete</span>;
    } else {
      return <span className="status-badge empty">○ Empty</span>;
    }
  };

  const getModulesBySection = (config, sectionName) => {
    return config.modules.filter(m => m.section === sectionName);
  };

  const filteredModules = (modules) => {
    if (!searchQuery.trim()) return modules;
    const q = searchQuery.toLowerCase();
    return modules.filter(m => m.title.toLowerCase().includes(q));
  };

  if (!isAdmin) {
    return (
      <div className="video-manager-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can manage videos.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="video-manager-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading video data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-vm-container">
      {/* Header */}
      <div className="unified-vm-header">
        <h1 className="unified-vm-title">
          <span className="vm-icon">🎬</span>
          Unified Video Management Dashboard
        </h1>
        <p className="unified-vm-subtitle">
          Manage all videos across your entire platform from one place
        </p>
      </div>

      {/* Message Banner */}
      {message.text && (
        <div className={`vm-message vm-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Page Tabs */}
      <div className="unified-vm-page-tabs">
        {Object.entries(PAGE_CONFIGS).map(([key, config]) => (
          <button
            key={key}
            className={`unified-page-tab ${activePage === key ? "active" : ""}`}
            onClick={() => {
              setActivePage(key);
              setActiveTier(config.tiers[0]);
            }}
          >
            <span className="tab-title">{config.title}</span>
            <span className="tab-desc">{config.modules.length || "Dynamic"} items</span>
          </button>
        ))}
      </div>

      {/* Page Content */}
      <div className="unified-vm-content">
        <div className="unified-vm-content-header">
          <div className="content-header-left">
            <h2>{currentConfig.title}</h2>
            <p>{currentConfig.description}</p>
          </div>
          <div className="content-header-right">
            <input
              type="text"
              className="vm-search"
              placeholder="Search modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="save-btn-primary"
              onClick={() => handleSave(activePage)}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                <>💾 Save {currentConfig.title}</>
              )}
            </button>
          </div>
        </div>

        {/* Tier Tabs (if applicable) */}
        {currentConfig.tiers.length > 1 && (
          <div className="vm-tabs">
            {currentConfig.tiers.map(tier => (
              <button
                key={tier}
                className={`vm-tab ${activeTier === tier ? "active" : ""}`}
                onClick={() => setActiveTier(tier)}
              >
                <span className="tab-icon">
                  {tier === "free" ? "🆓" : tier === "paid" ? "💎" : "📹"}
                </span>
                {tier.charAt(0).toUpperCase() + tier.slice(1)} Users
              </button>
            ))}
          </div>
        )}

        {/* Sections */}
        {currentConfig.useDynamicSections ? (
          // File Vault: Dynamic sections with items
          <div className="vm-sections">
            {currentConfig.sections.map((section) => {
              const sectionKey = `${activePage}-${section.name}`;
              const isExpanded = expandedSections[sectionKey];
              const items = data[activePage]?.[section.name] || [];

              return (
                <div key={sectionKey} className="vm-section">
                  <button
                    className={`section-header ${isExpanded ? "expanded" : ""}`}
                    onClick={() => toggleSection(sectionKey)}
                  >
                    <span className="section-icon">{section.icon}</span>
                    <span className="section-name">{section.name}</span>
                    <span className="section-count">{items.length} items</span>
                    <button
                      className="add-item-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        addFileVaultItem(activePage, section.name);
                      }}
                    >
                      + Add Item
                    </button>
                    <span className="section-chevron">{isExpanded ? "▼" : "▶"}</span>
                  </button>

                  {isExpanded && (
                    <div className="section-content">
                      {items.map((item) => (
                        <div key={item.id} className="file-vault-item-card">
                          <div className="item-header">
                            <span className="item-label">Item</span>
                            <button
                              className="delete-item-btn"
                              onClick={() => deleteFileVaultItem(activePage, section.name, item.id)}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                          <div className="item-body">
                            <div className="input-group">
                              <label>Title</label>
                              <input
                                type="text"
                                className="vm-input"
                                value={item.title || ""}
                                onChange={(e) =>
                                  updateFileVaultItem(activePage, section.name, item.id, "title", e.target.value)
                                }
                                placeholder="Enter title..."
                              />
                            </div>
                            <div className="input-group">
                              <label>URL / Link</label>
                              <input
                                type="text"
                                className="vm-input"
                                value={item.href || ""}
                                onChange={(e) =>
                                  updateFileVaultItem(activePage, section.name, item.id, "href", e.target.value)
                                }
                                placeholder="https://..."
                              />
                            </div>
                            <div className="input-group">
                              <label>Thumbnail URL</label>
                              <input
                                type="text"
                                className="vm-input"
                                value={item.thumb || ""}
                                onChange={(e) =>
                                  updateFileVaultItem(activePage, section.name, item.id, "thumb", e.target.value)
                                }
                                placeholder="Image URL..."
                              />
                            </div>
                            <div className="input-group">
                              <label>Display Type</label>
                              <select
                                className="vm-input"
                                value={item.type || "card"}
                                onChange={(e) =>
                                  updateFileVaultItem(activePage, section.name, item.id, "type", e.target.value)
                                }
                              >
                                <option value="card">Card (with thumbnail)</option>
                                <option value="row">Row (list style)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // Regular pages: Modules with videos
          <div className="vm-sections">
            {currentConfig.sections.map((section) => {
              const sectionKey = `${activePage}-${section.name}`;
              const isExpanded = expandedSections[sectionKey];
              const sectionModules = filteredModules(getModulesBySection(currentConfig, section.name));

              if (sectionModules.length === 0) return null;

              return (
                <div key={sectionKey} className="vm-section">
                  <button
                    className={`section-header ${isExpanded ? "expanded" : ""}`}
                    onClick={() => toggleSection(sectionKey)}
                  >
                    <span className="section-icon">{section.icon}</span>
                    <span className="section-name">{section.name}</span>
                    <span className="section-count">{sectionModules.length} modules</span>
                    <span className="section-chevron">{isExpanded ? "▼" : "▶"}</span>
                  </button>

                  {isExpanded && (
                    <div className="section-content">
                      {sectionModules.map((module) => {
                        const moduleData = data[activePage]?.[activeTier]?.[module.id] || {};
                        const urlValidation = validateAndConvertUrl(moduleData.url || "");
                        const uploadKey = `${activePage}-${activeTier}-${module.id}`;
                        const progress = uploadProgress[uploadKey] || 0;

                        return (
                          <div key={module.id} className="module-card">
                            <div className="module-header">
                              <div className="module-title-row">
                                <span className="module-id">{module.id.toUpperCase()}</span>
                                <span className="module-title">{module.title}</span>
                                {getStatusBadge(activePage, activeTier, module.id)}
                              </div>
                            </div>

                            <div className="module-body">
                              <div className="input-group">
                                <label className="input-label">
                                  <span>Video Title</span>
                                </label>
                                <input
                                  type="text"
                                  className="vm-input"
                                  value={moduleData.title || ""}
                                  onChange={(e) =>
                                    updateModuleField(activePage, activeTier, module.id, "title", e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  placeholder="Enter video title..."
                                />
                              </div>

                              <div className="input-group">
                                <label className="input-label">
                                  <span>Video URL</span>
                                  {urlValidation.type !== "empty" && (
                                    <span className={`url-type url-type-${urlValidation.type}`}>
                                      {urlValidation.message}
                                    </span>
                                  )}
                                </label>
                                <input
                                  type="text"
                                  className="vm-input"
                                  value={moduleData.url || ""}
                                  onChange={(e) =>
                                    updateModuleField(activePage, activeTier, module.id, "url", e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  placeholder="https://..."
                                />
                              </div>

                              <div className="input-group">
                                <label className="input-label">
                                  <span>Description</span>
                                </label>
                                <input
                                  type="text"
                                  className="vm-input"
                                  value={moduleData.description || ""}
                                  onChange={(e) =>
                                    updateModuleField(activePage, activeTier, module.id, "description", e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  placeholder="Enter description..."
                                />
                              </div>

                              <div className="module-actions">
                                <label className="upload-btn">
                                  📤 Upload Video
                                  <input
                                    type="file"
                                    accept="video/*"
                                    onChange={(e) =>
                                      handleVideoUpload(activePage, activeTier, module.id, e.target.files[0])
                                    }
                                    style={{ display: "none" }}
                                  />
                                </label>
                                {moduleData.url && (
                                  <a
                                    href={moduleData.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="preview-btn"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    👁️ Preview
                                  </a>
                                )}
                              </div>

                              {progress > 0 && (
                                <div className="upload-progress">
                                  <div className="progress-bar">
                                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                                  </div>
                                  <span className="progress-text">{Math.round(progress)}%</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

